import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';

export interface DeductionItem {
  inventoryItemId: string;
  name: string;
  previousQuantity: number | undefined;
  previousUnit: string | undefined;
  previousStatus: string | undefined;
}

export interface CookingTimerState {
  rem: number; // remaining seconds
  running: boolean;
}

export interface CookingSessionState {
  mealId: string | null;
  date: string | null; // YYYY-MM-DD
  recipeId: string | null; // set for standalone cooking mode (no meal plan)
  screen: 'planning' | 'cooking' | 'done';
  stepIndex: number;
  eatTimeMin: number; // minutes since midnight, default 1170 (19:30)
  timers: Record<number, CookingTimerState>; // keyed by step index
  startedAt: string | null; // ISO timestamp
  deductionSnapshot: DeductionItem[] | null; // pre-deduction state for undo
}

const initialState: CookingSessionState = {
  mealId: null,
  date: null,
  recipeId: null,
  screen: 'planning',
  stepIndex: 0,
  eatTimeMin: 1170,
  timers: {},
  startedAt: null,
  deductionSnapshot: null,
};

const cookingSessionSlice = createSlice({
  name: 'cookingSession',
  initialState,
  reducers: {
    startSession(
      state,
      action: PayloadAction<{ mealId: string; date: string }>
    ) {
      state.mealId = action.payload.mealId;
      state.date = action.payload.date;
      state.recipeId = null;
      state.screen = 'planning';
      state.stepIndex = 0;
      state.eatTimeMin = 1170;
      state.timers = {};
      state.startedAt = null;
      state.deductionSnapshot = null;
    },

    startStandaloneSession(
      state,
      action: PayloadAction<{ recipeId: string }>
    ) {
      state.recipeId = action.payload.recipeId;
      state.mealId = null;
      state.date = null;
      state.screen = 'cooking';
      state.stepIndex = 0;
      state.timers = {};
      state.startedAt = new Date().toISOString();
      state.deductionSnapshot = null;
    },

    resumeSession(
      state,
      action: PayloadAction<{ mealId: string; date: string }>
    ) {
      // No-op if mealId+date already match (idempotent)
      if (
        state.mealId === action.payload.mealId &&
        state.date === action.payload.date
      ) {
        return;
      }
      // Otherwise, treat as starting a new session
      state.mealId = action.payload.mealId;
      state.date = action.payload.date;
      state.recipeId = null;
      state.screen = 'planning';
      state.stepIndex = 0;
      state.eatTimeMin = 1170;
      state.timers = {};
      state.startedAt = null;
      state.deductionSnapshot = null;
    },

    advanceStep(state) {
      state.stepIndex += 1;
    },

    previousStep(state) {
      state.stepIndex = Math.max(0, state.stepIndex - 1);
    },

    goToStep(state, action: PayloadAction<number>) {
      state.stepIndex = Math.max(0, Math.min(action.payload, 1000));
    },

    updateEatTime(state, action: PayloadAction<number>) {
      state.eatTimeMin = action.payload;
    },

    setTimerDuration(
      state,
      action: PayloadAction<{ stepIndex: number; seconds: number }>
    ) {
      const { stepIndex, seconds } = action.payload;
      if (!state.timers[stepIndex]) {
        state.timers[stepIndex] = { rem: 0, running: false };
      }
      state.timers[stepIndex].rem = seconds;
    },

    startTimer(state, action: PayloadAction<{ stepIndex: number }>) {
      const { stepIndex } = action.payload;
      if (!state.timers[stepIndex]) {
        state.timers[stepIndex] = { rem: 0, running: false };
      }
      state.timers[stepIndex].running = true;
    },

    pauseTimer(state, action: PayloadAction<{ stepIndex: number }>) {
      const { stepIndex } = action.payload;
      if (state.timers[stepIndex]) {
        state.timers[stepIndex].running = false;
      }
    },

    tickTimers(state) {
      Object.keys(state.timers).forEach((key) => {
        const stepIndex = Number(key);
        const timer = state.timers[stepIndex];
        if (timer.running && timer.rem > 0) {
          timer.rem -= 1;
        }
      });
    },

    setScreen(
      state,
      action: PayloadAction<'planning' | 'cooking' | 'done'>
    ) {
      state.screen = action.payload;
    },

    endSession(state) {
      state.mealId = null;
      state.date = null;
      state.recipeId = null;
      state.screen = 'planning';
      state.stepIndex = 0;
      state.eatTimeMin = 1170;
      state.timers = {};
      state.startedAt = null;
      state.deductionSnapshot = null;
    },

    abandonSession(state) {
      state.mealId = null;
      state.date = null;
      state.recipeId = null;
      state.screen = 'planning';
      state.stepIndex = 0;
      state.eatTimeMin = 1170;
      state.timers = {};
      state.startedAt = null;
      state.deductionSnapshot = null;
    },

    saveDeductionSnapshot(state, action: PayloadAction<DeductionItem[]>) {
      state.deductionSnapshot = action.payload;
    },

    clearDeductionSnapshot(state) {
      state.deductionSnapshot = null;
    },
  },
});

export const {
  startSession,
  startStandaloneSession,
  resumeSession,
  advanceStep,
  previousStep,
  goToStep,
  updateEatTime,
  setTimerDuration,
  startTimer,
  pauseTimer,
  tickTimers,
  setScreen,
  endSession,
  abandonSession,
  saveDeductionSnapshot,
  clearDeductionSnapshot,
} = cookingSessionSlice.actions;

export default cookingSessionSlice.reducer;

// Selectors
export const selectCookingSession = (state: RootState): CookingSessionState =>
  state.cookingSession;

export const selectHasActiveCookingSession = (state: RootState): boolean => {
  const session = state.cookingSession;
  return session.mealId !== null && session.screen !== 'done';
};
